'use strict';

import { dwService } from 'amb/services/dw.service';
import { sharedStorage } from '../utils/sharedStorage';
import { tabIdentity } from '../utils/tabIdentity';
import { tabPresence } from './tabPresence';

/**
 * Server Tab Location sync via `Tab-Reconcile` (multi-cart).
 *
 * **Purpose:** Periodically tell the server which Client Tab Ids are still
 * alive so stale Tab Records can be cleaned up.
 *
 * **Scope:**
 * - Cross-tab debounce (`RECONCILE_AT_KEY` / `scheduleReconcile`)
 * - POST `Tab-Reconcile` with `collectLiveTabs` results (`reconcile`)
 *
 * **Out of scope:** Idle / visibility subscribe (global `components/coordinator`);
 * Presence probing (`tabPresence`); Checkout Ownership UX; Save Execution Gate;
 * CSRF; title attention.
 */
export const tabReconcile = {
  /**
   * @readonly
   * @private
   */
  STATE_KEY: 'reconcile',

  /**
   * Cross-tab reconcile throttle timestamp key.
   *
   * @readonly
   * @private
   */
  RECONCILE_AT_KEY: 'mcReconcileAt',

  /**
   * Minimum ms between Tab-Reconcile calls (shared across tabs via localStorage).
   *
   * @readonly
   * @private
   */
  RECONCILE_DEBOUNCE_MS: 15000,

  /**
   * @private
   *
   * @returns {{ initialized: boolean, lastReconcileAt: number }}
   */
  get state() {
    return sharedStorage.get(this.STATE_KEY, () => ({
      initialized: false,
      lastReconcileAt: 0
    }));
  },

  /**
   * Whether idle/visibility reconcile triggers have been registered.
   *
   * @returns {boolean}
   */
  isInitialized() {
    return !!this.state.initialized;
  },

  /**
   * Mark reconcile triggers as registered (idempotent wirer guard).
   */
  setInitialized() {
    this.state.initialized = true;
  },

  /**
   * Debounces reconcile across all storefront tabs (localStorage + memory).
   * No queue — skipped triggers wait for the next idle/visibility event.
   */
  scheduleReconcile() {
    const now = Date.now();
    const last = Math.max(this.state.lastReconcileAt || 0, this.getLastReconcileAt());

    if (last && now - last < this.RECONCILE_DEBOUNCE_MS) {
      return;
    }

    this.setLastReconcileAt(now);
    this.reconcile();
  },

  /**
   * @private
   *
   * @returns {number} epoch ms of last cluster-wide reconcile attempt
   */
  getLastReconcileAt() {
    try {
      const raw = localStorage.getItem(this.RECONCILE_AT_KEY);
      const value = raw ? parseInt(raw, 10) : 0;

      return isNaN(value) ? 0 : value;
    } catch (e) {
      return this.state.lastReconcileAt || 0;
    }
  },

  /**
   * @private
   *
   * @param {number} timestamp
   */
  setLastReconcileAt(timestamp) {
    this.state.lastReconcileAt = timestamp;

    try {
      localStorage.setItem(this.RECONCILE_AT_KEY, String(timestamp));
    } catch (e) {
      // Memory fallback only when storage is unavailable.
    }
  },

  /**
   * Probes live tabs and posts them to Tab-Reconcile. Failures are ignored.
   *
   * @private
   *
   * @returns {Promise<*>}
   */
  reconcile() {
    return tabPresence
      .collectLiveTabs(600)
      .then(liveTabs => $.ajax({
        url: dwService.url('Tab-Reconcile'),
        method: 'post',
        dataType: 'json',
        data: {
          mcTab: tabIdentity.peekTabId(),
          liveTabs: JSON.stringify(liveTabs)
        }
      }))
      .catch(() => null)
    ;
  }
};
